<template>
  <div class="sf-home">
    <!-- eyebrow + título + refresh agora vivem no navbar -->

    <!-- 1. Faixa de mercado (TradingView) -->
    <div
      v-if="windowWidth >= 800"
      class="sf-market mb-3"
    >
      <TradingViewWidgets :options="{ theme: isThemeDark ? 'dark' : 'light' }" />
    </div>

    <termo-de-uso />

    <!-- 2. Valor estimado + Crédito da taxa -->
    <div class="sf-grid sf-grid-main mb-3">
      <!-- Valor estimado + alocação -->
      <div class="sf-card sf-pad">
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('futures.textValorEstimado') }}</span>
        </div>

        <template v-if="loadingOverlayCarteira && errorLoadCarteira == null">
          <b-skeleton
            width="180px"
            height="34px"
            class="mb-3"
            animation="wave"
          />
          <b-skeleton
            width="100%"
            height="180px"
            animation="wave"
          />
        </template>

        <template v-else-if="errorLoadCarteira == null">
          <div class="sf-value mb-1">
            <span class="sf-value-cur">$</span>
            <span class="sf-value-amt sf-num">{{ estimativaUSD }}</span>
          </div>
          <div class="sf-alloc">
            <div class="sf-alloc-chart">
              <vue-apex-charts
                type="donut"
                height="200"
                :options="carteiraGrafico"
                :series="seriesCarteira"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="sf-errorstate">
            <feather-icon
              icon="AlertTriangleIcon"
              size="40"
            />
            <p>{{ errorLoadCarteira }}</p>
          </div>
        </template>
      </div>

      <!-- Crédito da taxa -->
      <div
        v-if="creditoDesempenho"
        class="sf-card sf-pad sf-credit"
      >
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('futures.textCreditoTaxa') }}</span>
        </div>
        <div class="sf-value mb-1">
          <span class="sf-value-cur">$</span>
          <span class="sf-value-amt sf-num">{{ toFixed(creditoDesempenho.saldo, 2) }}</span>
        </div>
        <div class="sf-credit-sub">
          {{ $t('futures.textCreditoTaxaSub') }}
        </div>
        <div class="sf-credit-actions">
          <router-link
            class="sf-btn sf-btn-primary"
            :to="{ name: 'checkout-credito' }"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            {{ $t('futures.textAdicionarSaldo') }}
          </router-link>
          <b-link
            class="sf-btn sf-btn-secondary"
            :to="{ name: 'conta-credito-extrato' }"
          >
            <feather-icon
              icon="FileTextIcon"
              size="16"
            />
            {{ $t('futures.textExtrato') }}
          </b-link>
        </div>
      </div>
    </div>

    <!-- 3. Carteira + Ganhos do mês + Assertividade -->
    <div class="sf-grid sf-grid-three mb-3">
      <!-- Carteira -->
      <div class="sf-card sf-pad">
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('futures.titleCardCarteira') }}</span>
        </div>
        <div class="sf-coins">
          <div
            v-for="car in carteira"
            :key="car.simbolo"
            class="sf-coin"
          >
            <b-avatar
              :src="getIcon(car.simbolo)"
              size="36px"
              style="background-color: transparent"
            />
            <div class="sf-coin-info">
              <div class="sf-coin-sym">
                {{ car.simbolo }}
              </div>
              <div class="sf-coin-val sf-num">
                {{ toFixed(car.valor, 6) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ganhos do mês -->
      <div class="sf-card sf-pad">
        <div class="sf-card-head sf-head-row">
          <span class="sf-eyebrow">{{ $t('futures.titleCardFinanceiro') }}</span>
          <span
            v-if="cardFinanceirodiferencaPercentual"
            class="sf-num sf-diff"
          >{{ cardFinanceirodiferencaPercentual }}%</span>
        </div>
        <div class="sf-value mb-2">
          <span class="sf-value-cur">$</span>
          <span class="sf-value-amt sf-value-amt-sm sf-num">{{ cardFinanceiroGanhoMesAtual }}</span>
        </div>
        <vue-apex-charts
          type="line"
          height="180"
          :options="cardFinanceiroGanhoMesesChartOptions"
          :series="cardFinanceiroGanhoMesesSeries.series"
        />
        <div
          v-if="cardFinanceirodiferencaPercentual"
          class="sf-sub"
        >
          <span class="sf-num">{{ cardFinanceirodiferencaPercentual }}%</span> {{ $t('futures.subtitleCardFinanceiro') }}
        </div>
      </div>

      <!-- Assertividade do mês -->
      <div class="sf-card sf-pad">
        <div class="sf-card-head">
          <span class="sf-eyebrow">{{ $t('futures.titleCardAssertividadeMensal') }}</span>
        </div>
        <grafico-sinal-fut
          :resumo-sinal="resumoSinal"
          :series="seriesSinal"
        />
      </div>
    </div>

    <!-- 4. Ativo USDT -->
    <div class="sf-section mb-3">
      <div class="sf-card-head sf-usdt-head">
        <span class="sf-usdt-chip">$</span>
        <span class="sf-eyebrow">{{ $t('futures.titleCardInvestimento') }}</span>
      </div>
      <div class="sf-kpis">
        <div class="sf-kpi">
          <div class="sf-kpi-top">
            <span class="sf-eyebrow">{{ $t('futures.textSaldo') }}</span>
            <span class="sf-kpi-icon sf-kpi-icon-green">
              <feather-icon
                icon="DollarSignIcon"
                size="16"
              />
            </span>
          </div>
          <div
            v-show="saldoTotalUSDTCarteira"
            class="sf-kpi-val sf-num sf-up"
          >{{ toFixed(saldoTotalUSDTCarteira, 2) }}</div>
        </div>
        <div class="sf-kpi">
          <div class="sf-kpi-top">
            <span class="sf-eyebrow">{{ $t('futures.textSaldoMargem') }}</span>
            <span class="sf-kpi-icon">
              <feather-icon
                icon="BarChart2Icon"
                size="16"
              />
            </span>
          </div>
          <div
            v-show="saldoMargemUSDTCarteira"
            class="sf-kpi-val sf-num"
          >{{ parseFloat(saldoMargemUSDTCarteira).toFixed(2) }}</div>
        </div>
        <div class="sf-kpi">
          <div class="sf-kpi-top">
            <span class="sf-eyebrow">{{ $t('futures.textOperacoesAbertas') }}</span>
            <span class="sf-kpi-icon">
              <feather-icon
                icon="ActivityIcon"
                size="16"
              />
            </span>
          </div>
          <div
            v-show="unPnlUSDTCarteira"
            class="sf-kpi-val sf-num"
          >{{ parseFloat(unPnlUSDTCarteira).toFixed(2) }}</div>
        </div>
        <div class="sf-kpi">
          <div class="sf-kpi-top">
            <span class="sf-eyebrow">{{ $t('futures.textDisponivelOperar') }}</span>
            <span class="sf-kpi-icon">
              <feather-icon
                icon="TrendingUpIcon"
                size="16"
              />
            </span>
          </div>
          <div
            v-if="disponivelUSD"
            class="sf-kpi-val sf-num"
          >{{ toFixed(disponivelUSD, 2) }}</div>
        </div>
      </div>
    </div>

    <!-- 5. Operações recentes -->
    <div class="sf-card sf-table-card">
      <div class="sf-table-title">
        {{ $t('futures.titleTerceiroCard') }}
      </div>
      <div class="sf-scroll">
        <template v-if="loadingOverlayCarteira">
          <b-skeleton-table
            :rows="6"
            :columns="tableColumns.length"
            animation="wave"
            class="p-2"
          />
        </template>
        <b-table
          v-else
          ref="refListTable"
          :items="dataListUltimasVendas"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          :empty-text="$t('futures.emptyText')"
          class="sf-table"
        >
          <template #cell(paridade)="data">
            <div class="sf-cell-pair">
              <b-avatar
                size="sm"
                :src="getIcon(data.item.moedaTrade)"
                style="background-color: transparent"
              />
              <span class="sf-pair-base">{{ data.item.moedaTrade }}</span>
            </div>
          </template>

          <template #cell(ordemPosition)="data">
            <div class="sf-cell-position">
              <span>{{ data.value }}</span>
              <span class="sf-lev-pill sf-num">{{ data.item.alavancagem }}x</span>
            </div>
          </template>

          <template #cell(resultado)="data">
            <div class="sf-cell-pnl">
              <span
                :class="parseFloat(data.item.pnl) >= 0 ? 'sf-up' : 'sf-down'"
                class="sf-num"
              >
                <feather-icon
                  :icon="parseFloat(data.item.pnl) >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'"
                  size="13"
                />
                {{ toFixed(data.item.pnl, 4) }} {{ data.item.paridadeTrade }}
              </span>
            </div>
          </template>

          <template #cell(duracao)="data">
            <span class="sf-num sf-muted">{{ duracao(data.item.dataCriacaoCompra, data.item.dataExecucao) }}</span>
          </template>

          <template #cell(dataCriacaoCompra)="data">
            <div class="sf-cell-date">
              <span class="sf-num">{{ formatDateBR(data.value) }}</span>
              <span class="sf-num sf-muted">{{ formatHourBR(data.value) }}</span>
            </div>
          </template>

          <template #cell(agente)="data">
            <div class="sf-cell-agent">
              <span>{{ data.value }}</span>
              <span class="sf-signal-pill sf-num">#{{ data.item.sinalId }}</span>
            </div>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BTable, BAvatar, VBTooltip, BLink, BSkeleton, BSkeletonTable,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import TradingViewWidgets from '@/layouts/components/custom/TradingViewWidgets.vue'
import store from '@/store'
import utilsMixin from '@/mixins/utilsMixin'
import {
  formatHourBR, formatDateBR, percentDiferenca, duracao, toIsoStringTimeZone,
} from '@/utils/filter'
import Ripple from 'vue-ripple-directive'
import fechamentoStoreModule from './relatorio/fechamento/fechamentoStoreModule'
import posicaoStoreModule from './posicao/posicaoStoreModule'
import usuarioStoreModule from './usuario/usuarioStoreModule'
import TermoDeUso from '../termos/TermoDeUso.vue'

const RELATORIO_FECHAMENTO_FUT_STORE_MODULE_NAME = 'fechamento-relatorio-fut'
const POSICAO_FUT_STORE_MODULE_NAME = 'posicao-fut'
const USUARIO_FUT_STORE_MODULE_NAME = 'usuario-fut'

export default {
  components: {
    BTable,
    BAvatar,
    TradingViewWidgets,
    VueApexCharts,
    BLink,
    BSkeleton,
    BSkeletonTable,
    TermoDeUso,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  mixins: [utilsMixin],

  data() {
    return {
      errorLoadCarteira: null,
      loadingOverlayCarteira: false,
      loadingOverlaySinal: false,
      loadingOverlayFinanceiro: false,
      formatHourBR,
      formatDateBR,
      percentDiferenca,
      duracao,
      dataListUltimasVendas: [],
      resumoSinal: [],
      seriesSinal: [],
      estimativaUSD: '',
      atualizacaoCarteira: '',
      dataAtual: new Date(),
      seriesCarteira: [],
      labelsCarteira: [],
      carteira: [],
      creditoDesempenho: null,
      saldoTotalUSDTCarteira: '',
      saldoMargemUSDTCarteira: '',
      unPnlUSDTCarteira: '',
      dataListFinanceiro: [],
      cardFinanceiroGanhoMesAtual: '',
      cardFinanceiroGanhoMesChart: {
        series: [100, 100],
        chartOptions: {
          legend: {
            show: false,
          },
          colors: [
            '#288D70',
            '#2B7461',
          ],
          dataLabels: {
            enabled: false,
            formatter(val) {
              // eslint-disable-next-line radix
              return `${parseInt(val)}%`
            },
          },
          plotOptions: {
            pie: {
              startAngle: -90,
              endAngle: 270,
              expandOnClick: false,
              donut: {
                labels: {
                  show: true,
                  name: {
                    fontSize: '12px',
                    fontFamily: 'Geist',
                  },
                  value: {
                    fontSize: '12px',
                    formatter(val) {
                      // eslint-disable-next-line radix
                      return `${parseInt(val)}%`
                    },
                  },
                  total: {
                    show: true,
                    fontSize: '12px',
                    label: 'Total',
                    formatter(val) {
                      // eslint-disable-next-line radix
                      return `${val.config.series[0]}%`
                    },
                  },
                },
              },
            },
          },
          labels: ['Total', ''],
        },
      },
      cardFinanceiroGanhoMesesCategories: [],
      cardFinanceiroGanhoMesesSeries: {
        series: [
          {
            name: 'USDT',
            data: [],
          },
        ],
      },
    }
  },

  computed: {
    tableColumns() {
      return [
        {
          key: 'paridade', label: this.$i18n.t('futures.tableHeader.paridade'), tdClass: 'text-dark recentTransactionCell',
        },
        {
          key: 'ordemPosition', label: this.$i18n.t('futures.tableHeader.ordemPosition'), tdClass: 'text-dark recentTransactionCell',
        },
        {
          key: 'quantidade', label: this.$i18n.t('futures.tableHeader.quantidade'), tdClass: 'text-dark recentTransactionCell',
        },
        {
          key: 'resultado', label: this.$i18n.t('futures.tableHeader.resultado'), tdClass: 'text-dark recentTransactionCell text-center', thClass: 'text-center',
        },
        {
          key: 'dataCriacaoCompra', label: this.$i18n.t('futures.tableHeader.dataCriacaoCompra'), tdClass: 'text-dark recentTransactionCell text-center', thClass: 'text-center',
        },
        {
          key: 'duracao', label: this.$i18n.t('futures.tableHeader.duracao'), tdClass: 'text-dark recentTransactionCell',
        },
        {
          key: 'agente', label: this.$i18n.t('futures.tableHeader.trader'), tdClass: 'text-dark recentTransactionCell',
        },
      ]
    },

    carteiraAtualizacaoElapsed() {
      if (this.atualizacaoCarteira != null) {
        return this.elapsedTime2(this.atualizacaoCarteira, this.dataAtual)
      }
      return null
    },

    isThemeDark() {
      return this.$store.state.appConfig.layout.skin === 'dark'
    },

    windowWidth() {
      return this.$store.state.app.windowWidth
    },

    carteiraGraficoBar() {
      const chartOptions = {
        chart: {
          stacked: true,
          toolbar: {
            show: false,
          },
        },
        colors: ['#26A17B', '#F3BA2F', '#00e396', '#82868b', '#775dd0'],
        plotOptions: {
          bar: {
            distributed: true,
            columnWidth: '10%',
            colors: {
              backgroundBarColors: [
                '#313A49',
                '#313A49',
                '#313A49',
                '#313A49',
                '#313A49',
                '#313A49',
                '#313A49',
                '#313A49',
              ],
              backgroundBarRadius: 7,
            },
          },
        },
        labels: this.labelsCarteira,
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
          position: 'top',
          fontSize: '14px',
          fontFamily: 'Geist',
          horizontalAlign: 'left',
        },
        stroke: {
          show: true,
          colors: ['transparent'],
        },
        grid: {
          show: false,
        },
        xaxis: {
          categories: this.labelsCarteira,
          lines: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          // opposite: isRtl,
          categories: [''],
          labels: {
            show: false,
          },

        },
        fill: {
          opacity: 1,
        },
      }
      return chartOptions
    },

    carteiraGrafico() {
      const chartOptions = {
        noData: {
          text: 'Loading...',
        },
        labels: this.labelsCarteira,
        dataLabels: {
          enabled: false,
          formatter(val) {
            // eslint-disable-next-line radix
            return `${parseInt(val)}%`
          },
        },
        colors: ['#3b82f6', '#3f4249', '#2b2e33', '#1C1C1E', '#121214', '#3f4249'],
        stroke: {
          show: true,
          width: 3,
          colors: ['#0F0F11'],
        },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '12px',
          fontFamily: 'Geist',
          formatter(seriesName, opts) {
            return [`<span class='font-weight-bolder'>${seriesName} - </span>`, '  ', parseFloat(opts.w.globals.series[opts.seriesIndex]).toFixed(4)]
          },
        },
        chart: {
          type: 'donut',
        },
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 270,
            expandOnClick: false,
            donut: {
              labels: {
                show: false,
                name: {
                  fontSize: '12px',
                  fontFamily: 'Geist',
                },
                value: {
                  show: false,
                  fontSize: '12px',
                  formatter(val) {
                  // eslint-disable-next-line radix
                    return `${parseInt(val)}`
                  },
                },
                total: {
                  show: false,
                  fontSize: '12px',
                  label: 'Total',
                  formatter(val) {
                  // eslint-disable-next-line radix
                    return `${val.config.series[0]}`
                  },
                },
              },
            },
          },
        },
      }
      return chartOptions
    },

    cardFinanceiroGanhoMesesChartOptions() {
      const chartOptions = {
        noData: {
          text: 'Loading...',
        },
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          type: 'line',
          dropShadow: {
            enabled: true,
            top: 18,
            left: 2,
            blur: 5,
            opacity: 0.2,
          },
          offsetX: -10,
        },
        stroke: {
          curve: 'smooth',
          width: 4,
        },
        grid: {
          show: true,
          borderColor: 'rgba(255,255,255,0.06)',
          strokeDashArray: 0,
          position: 'back',
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: false,
            },
          },
          row: {
            colors: undefined,
            opacity: 0.5,
          },
          column: {
            colors: undefined,
            opacity: 0.5,
          },
        },
        legend: {
          show: false,
        },
        colors: ['#4ade80'],
        markers: {
          size: 5,
          hover: {
            size: 5,
          },
        },
        xaxis: {
          labels: {
            offsetY: 5,
            style: {
              colors: '#b9b9c3',
              fontSize: '0.857rem',
            },
          },
          categories: this.cardFinanceiroGanhoMesesCategories,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            gradientToColors: ['#4ade80'],
            shadeIntensity: 1,
            type: 'horizontal',
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100, 100, 100],
          },
        },

        yaxis: {
          tickAmount: 5,
          labels: {
            style: {
              colors: '#b9b9c3',
              fontSize: '0.857rem',
            },
            formatter(val) {
              return val > 999 ? `${(val / 1000).toFixed(1)}k` : parseFloat(val).toFixed(2)
            },
          },
        },
      }
      return chartOptions
    },

    cardFinanceirodiferencaPercentual() {
      const desempenho = this.dataListFinanceiro
      const keys = Object.keys(desempenho)
      if (keys.length < 2) {
        return 'Insufficient data'
      }

      const ultimoMes = keys[keys.length - 1]
      const penultimoMes = keys[keys.length - 2]

      const valorUltimoMes = desempenho[ultimoMes]
      const valorPenultimoMes = desempenho[penultimoMes]

      if (valorPenultimoMes === 0) {
        return 'Cannot calculate '
      }

      const diferenca = valorUltimoMes - valorPenultimoMes
      const diferencaPercentual = (diferenca / valorPenultimoMes) * 100

      // Arredonda o valor para 2 casas decimais
      return `${diferencaPercentual.toFixed(2)}`
    },

  },

  created() {
    if (!store.hasModule(USUARIO_FUT_STORE_MODULE_NAME)) store.registerModule(USUARIO_FUT_STORE_MODULE_NAME, usuarioStoreModule)
    this.isVerificaAssinaturaAtiva()
    if (!store.hasModule(POSICAO_FUT_STORE_MODULE_NAME)) store.registerModule(POSICAO_FUT_STORE_MODULE_NAME, posicaoStoreModule)
    if (!store.hasModule(RELATORIO_FECHAMENTO_FUT_STORE_MODULE_NAME)) store.registerModule(RELATORIO_FECHAMENTO_FUT_STORE_MODULE_NAME, fechamentoStoreModule)
    this.pollDataAtual()
    this.getCarteira(true)
    this.getPosicaoResumo()
    this.getUltimasPosicoesFinalizadas()
    this.getFinanceiroResumoHome()
  },

  beforeDestroy() {
    if (store.hasModule(POSICAO_FUT_STORE_MODULE_NAME)) store.unregisterModule(POSICAO_FUT_STORE_MODULE_NAME)
    if (store.hasModule(RELATORIO_FECHAMENTO_FUT_STORE_MODULE_NAME)) store.unregisterModule(RELATORIO_FECHAMENTO_FUT_STORE_MODULE_NAME)
    if (store.hasModule(USUARIO_FUT_STORE_MODULE_NAME)) store.unregisterModule(USUARIO_FUT_STORE_MODULE_NAME)
  },

  methods: {

    isVerificaAssinaturaAtiva() {
      store
        .dispatch('usuario-fut/isVerificaAssinaturaAtiva')
        .then(response => {
          if (response.data !== true) {
            this.$router.push({ name: 'conta-assinatura' })
          }
        })
    },

    getUltimasPosicoesFinalizadas() {
      store
        .dispatch('fechamento-relatorio-fut/getUltimasPosicoesFinalizadas')
        .then(response => {
          this.dataListUltimasVendas = response.data.content
        })
    },

    getPosicaoResumo() {
      this.loadingOverlaySinal = true

      const currentDate = new Date()
      const primeiroDiaMes = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      const ultimoDiaMes = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

      const dataInicio = toIsoStringTimeZone(primeiroDiaMes)
      const dataFim = toIsoStringTimeZone(ultimoDiaMes)

      store
        .dispatch('posicao-fut/getPosicaoResumoHome', {
          dataInicio,
          dataFim,
        })
        .then(response => {
          this.seriesSinal = [response.data.totalAtivoPercent, response.data.totalGanhoPercent, response.data.totalPerdaPercent]
          this.resumoSinal = response.data
        })
        .finally(() => {
          this.loadingOverlaySinal = false
        })
    },

    getFinanceiroResumoHome() {
      this.loadingOverlayFinanceiro = true

      store
        .dispatch('fechamento-relatorio-fut/getFinanceiroResumoHome')
        .then(response => {
          this.dataListFinanceiro = response.data.desempenho
          const chaves = Object.keys(this.dataListFinanceiro)
          const ultimaChave = this.dataListFinanceiro[chaves[chaves.length - 1]]
          this.cardFinanceiroGanhoMesAtual = parseFloat(ultimaChave || 0).toFixed(2)
          this.cardFinanceiroGanhoMesesCategories = chaves
          const seriesData = Object.values(this.dataListFinanceiro).map(value => Math.round(value))

          this.cardFinanceiroGanhoMesesSeries = {
            series: [{
              data: seriesData,
            }],
          }
        })
        .finally(() => {
          this.loadingOverlayFinanceiro = false
        })
    },

    atualizarDados() {
      this.getCarteira(false)
      this.getUltimasPosicoesFinalizadas()
      this.getPosicaoResumo()
    },

    getCarteira(cache) {
      this.loadingOverlayCarteira = true

      store
        .dispatch('usuario-fut/getCarteira', cache)
        .then(response => {
          this.errorLoadCarteira = null
          this.estimativaUSD = response.estimativaUSD
          this.atualizacaoCarteira = response.atualizacao
          this.saldoTotalUSDTCarteira = response.balanceUSD
          this.saldoMargemUSDTCarteira = response.marginBalanceUSD
          this.unPnlUSDTCarteira = response.unrealizedPNLUSD
          this.disponivelUSD = response.disponivelUSD
          this.saldoMargemUSDTCarteiraPerc = response.marginBalanceUSDPerc
          this.unPnlUSDTCarteiraPerc = response.unrealizedPNLUSDPerc
          this.disponivelUSDPerc = response.disponivelUSDPerc
          this.carteira = response.carteira
          const seriesPrincipal = []
          const labelsPrincipal = []

          if (response.carteira != null) {
            response.carteira.forEach(value => {
              seriesPrincipal.push(value.valor)
              labelsPrincipal.push(value.simbolo)
            })
          }
          this.seriesCarteira = seriesPrincipal
          this.labelsCarteira = labelsPrincipal
          // this.seriesBar = [{ data: seriesDataBar }]
        }).catch(e => {
          this.errorLoadCarteira = e.data
          this.estimativaUSD = 0
          this.atualizacaoCarteira = null
          this.saldoTotalUSDTCarteira = 0
          this.saldoMargemUSDTCarteira = 0
          this.unPnlUSDTCarteira = 0
          this.disponivelUSD = 0
          this.saldoMargemUSDTCarteiraPerc = 0
          this.unPnlUSDTCarteiraPerc = 0
          this.disponivelUSDPerc = 0
          this.seriesCarteira = []
          this.labelsCarteira = []
          this.seriesBar = []
          this.carteira = []
        }).finally(() => {
          this.loadingOverlayCarteira = false
        })

      store
        .dispatch('usuario-fut/getCreditoDesempenho', cache)
        .then(response => {
          this.creditoDesempenho = response.data
        }).catch(() => {
          this.creditoDesempenho = null
        })
    },

    pollDataAtual() {
      const self = this
      this.polling = setInterval(() => {
        self.dataAtual = new Date()
      }, 10000)
    },

  },
}
</script>

<style lang="scss" scoped>
/* ============================================================
   Soulf Home — futures (dark premium). Tokens em
   src/assets/scss/soulf-theme.scss (:root --sf-*).
   ============================================================ */
.sf-home {
  padding: 28px 32px 60px;
  max-width: 1640px;
  width: 100%;

  @media (max-width: 1200px) { padding: 20px 16px 48px; }
  @media (max-width: 768px)  { padding: 16px 12px 40px; }
}

.sf-market {
  background: var(--sf-card);
  border: 1px solid var(--sf-border);
  border-radius: 16px;
  padding: 12px 16px;
  overflow: hidden;
}

/* Grids */
.sf-grid { display: grid; gap: 20px; }
.sf-grid-main {
  grid-template-columns: minmax(0, 1fr) minmax(300px, 360px);
  @media (max-width: 992px) { grid-template-columns: 1fr; }
}
.sf-grid-three {
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

/* Card base */
.sf-card {
  background: var(--sf-card);
  border: 1px solid var(--sf-border);
  border-radius: 16px;
}
.sf-pad { padding: 24px; }
.sf-card-head { margin-bottom: 14px; }
.sf-head-row { display: flex; align-items: center; justify-content: space-between; }
.sf-section { display: block; }

/* Valor grande */
.sf-value {
  display: flex;
  align-items: baseline;
  gap: 4px;

  .sf-value-cur { font-size: 15px; color: var(--sf-fg-3); }
  .sf-value-amt { font-size: 30px; font-weight: 500; color: var(--sf-fg); line-height: 1.1; }
  .sf-value-amt-sm { font-size: 24px; }
}
.sf-sub {
  font-size: 12px;
  color: var(--sf-fg-4);
  margin-top: 8px;
  .sf-num { color: var(--sf-green); font-weight: 600; }
}
.sf-diff { font-size: 12px; font-weight: 600; color: var(--sf-green); }

.sf-alloc {
  display: flex;
  justify-content: center;
  .sf-alloc-chart { width: 100%; }
}

/* Crédito */
.sf-credit {
  display: flex;
  flex-direction: column;

  .sf-credit-sub { font-size: 12px; color: var(--sf-fg-4); margin-top: 2px; }

  .sf-credit-actions {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 20px;
  }
}
.sf-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: transform .12s ease, background .12s ease;

  &:hover { transform: translateY(-1px); text-decoration: none; }
  &.sf-btn-primary { background: var(--sf-accent); color: var(--sf-accent-on); }
  &.sf-btn-secondary {
    background: var(--sf-code);
    border: 1px solid var(--sf-border);
    color: var(--sf-fg-2);
    &:hover { color: #fff; }
  }
}

/* Cabeçalho da página */
.sf-pagehead {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  .sf-pagetitle {
    font-size: 22px;
    font-weight: 600;
    color: var(--sf-fg);
    letter-spacing: -0.01em;
    margin: 4px 0 0;
  }
  .sf-pagehead-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.sf-live {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: sfLive 2s infinite;
}
@keyframes sfLive {
  0%   { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
  70%  { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}

/* Indicador de atualização (chip no page-head) */
.sf-refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--sf-card);
  border: 1px solid var(--sf-border);
  border-radius: 9px;
  color: var(--sf-fg-3);
  font-size: 12px;
  padding: 7px 12px;
  cursor: pointer;
  transition: color .15s ease, border-color .15s ease;
  &:hover { color: #fff; border-color: var(--sf-border-strong); }
}

/* Carteira (lista de moedas) */
.sf-coins { display: flex; flex-direction: column; gap: 12px; }
.sf-coin {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--sf-card-2);
  border: 1px solid var(--sf-border);
  border-radius: 12px;
  padding: 14px 16px;

  .sf-coin-sym { font-size: 12px; color: var(--sf-fg-3); }
  .sf-coin-val { font-size: 16px; font-weight: 500; color: var(--sf-fg); }
}

/* Ativo USDT (KPIs) */
.sf-usdt-head { display: flex; align-items: center; gap: 10px; }
.sf-usdt-chip {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--sf-elev);
  border: 1px solid var(--sf-border);
  border-radius: 8px;
  color: #26A17B;
  font-family: 'Geist Mono','SFMono-Regular',Menlo,monospace;
  font-weight: 600;
}
.sf-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));
  gap: 16px;
}
.sf-kpi {
  background: var(--sf-card);
  border: 1px solid var(--sf-border);
  border-radius: 14px;
  padding: 18px 20px;

  .sf-kpi-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .sf-kpi-icon {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: var(--sf-elev);
    color: var(--sf-fg-3);
    &.sf-kpi-icon-green { background: rgba(34,197,94,0.12); color: var(--sf-green); }
  }
  .sf-kpi-val { font-size: 23px; font-weight: 500; color: var(--sf-fg); }
}

/* Card de tabela */
.sf-table-card { overflow: hidden; }
.sf-table-title {
  padding: 18px 24px;
  border-bottom: 1px solid var(--sf-border);
  font-size: 15px;
  font-weight: 600;
  color: var(--sf-fg);
}
.sf-scroll { overflow-x: auto; }
.sf-muted { color: var(--sf-fg-4); }

/* Células */
.sf-cell-pair {
  display: flex;
  align-items: center;
  gap: 8px;
  .sf-pair-base { font-weight: 500; color: var(--sf-fg); }
}
.sf-cell-position {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--sf-fg-2);
}
.sf-lev-pill {
  font-size: 10px;
  font-weight: 600;
  color: var(--sf-fg-3);
  border: 1px solid var(--sf-border);
  border-radius: 5px;
  padding: 2px 6px;
}
.sf-cell-pnl .sf-num { display: inline-flex; align-items: center; gap: 3px; }
.sf-cell-date {
  display: flex;
  flex-direction: column;
  span:last-child { font-size: 12px; }
}
.sf-cell-agent {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--sf-fg-3);
}
.sf-signal-pill {
  font-size: 10px;
  color: var(--sf-accent);
  background: rgba(255,255,255,0.05);
  border-radius: 5px;
  padding: 2px 6px;
}

.sf-errorstate {
  text-align: center;
  padding: 32px 12px;
  .feather { color: var(--sf-red); margin-bottom: 12px; }
  p { color: var(--sf-fg-4); margin-bottom: 0; }
}
</style>

<style lang="scss">
.sf-table.table { margin-bottom: 0; }
.sf-table.table thead th:first-child,
.sf-table.table tbody td:first-child { padding-left: 24px; }
.sf-table.table thead th:last-child,
.sf-table.table tbody td:last-child { padding-right: 24px; }
</style>
